import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Compass, Check, CornerUpLeft, Sparkles } from "lucide-react";
import { startFO, foCoach, completeFO } from "@/lib/api";
import CascadeOrientation from "@/components/CascadeOrientation";

// v1.3-style **bold** renderer so the core question in a coach message stands out.
const renderBold = (text) =>
  (text || "").split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith("**") && seg.endsWith("**")
      ? <strong key={i} className="font-semibold text-stone-900">{seg.slice(2, -2)}</strong>
      : <span key={i}>{seg}</span>
  );

const RELATION_HINT = {
  probable: "job not settled yet",
};

// FUNCTIONAL ORGANIZATION (Phase 2, Checkpoint 1). The multi-turn FO teaching stage between OT and
// Composition. The student is scaffolded through: seeing their thinking organized -> figuring out their
// thesis -> what each major part does for the thesis -> how the parts work for a reader -> using that to
// decide what to develop. Parts can change the thesis (STRUCTURE <-> CONTENT). The derived structure is
// PRIVATE intelligence — the panel shows the student's own worked-out functions, not a "confirm this?" form.
export default function FunctionalOrganization({ sessionId, onComplete, onBack, onStepBackToOT = null, onResumeToComposition = null, entryConfirmation = "" }) {
  const [fo, setFo] = useState(null);
  const [thread, setThread] = useState([]);
  const [input, setInput] = useState("");
  const [starting, setStarting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const endRef = useRef(null);
  const startedRef = useRef(false);

  const applyResult = useCallback((data) => {
    const f = data.fo || data;
    setFo(f);
    if (f && Array.isArray(f.turns)) setThread(f.turns);
  }, []);

  useEffect(() => {
    if (!sessionId || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const data = await startFO(sessionId);
        applyResult(data);
      } catch (e) {
        setThread([{ role: "tws", text: "I couldn't open this step just now — your work is safe. Please try again in a moment.", at: "" }]);
      } finally {
        setStarting(false);
      }
    })();
  }, [sessionId, applyResult]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread, busy]);

  const send = useCallback(async (message = "", event = "") => {
    if (busy || completing) return;
    if (!message.trim() && !event) return;
    setBusy(true);
    if (message.trim()) setInput("");
    try {
      const data = await foCoach(sessionId, message.trim(), event);
      applyResult(data);
    } catch (e) {
      setThread((t) => [...t, { role: "tws", text: "I had trouble just now — your work is safe. Please send that again.", at: "" }]);
    } finally {
      setBusy(false);
    }
  }, [sessionId, busy, completing, applyResult]);

  const finish = useCallback(async () => {
    if (completing) return;
    setCompleting(true);
    try {
      const res = await completeFO(sessionId);
      onComplete?.(res?.transition_message || "");
    } catch (e) {
      onComplete?.("");
    } finally {
      setCompleting(false);
    }
  }, [sessionId, completing, onComplete]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  const est = fo?.established || {};
  const units = fo?.units || [];
  const sufficient = fo?.sufficiency === "sufficient";
  const coordLabel = fo?.coordination_label || "";
  const returnContext = fo?.return_context || null;
  const focalPartDone = returnContext
    ? !!(units.find((u) => String(u.key) === String(returnContext.part_key))?.worked_out)
    : false;

  return (
    <div className="min-h-screen paper-grain flex flex-col items-center px-4 sm:px-6 py-6" data-testid="fo-root">
      <div className="w-full max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] flex items-center gap-1.5" data-testid="fo-kicker">
              <Compass className="h-3.5 w-3.5" /> Functional Organization
            </p>
            <h1 className="font-serif-display text-2xl sm:text-3xl leading-tight text-stone-900 mt-1.5">
              Turning your thinking into writing for a reader
            </h1>
          </div>
          {onBack ? (
            <button onClick={onBack} data-testid="fo-back" className="text-[13px] text-stone-500 hover:text-stone-900 flex items-center gap-1">
              <CornerUpLeft className="h-3.5 w-3.5" /> Thinking
            </button>
          ) : null}
        </div>

        <CascadeOrientation sessionId={sessionId} refreshKey={fo?.coordination} className="mb-5"
          onStepBack={onStepBackToOT ? (ret) => onStepBackToOT(ret) : null} />

        {returnContext ? (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            data-testid="fo-return-banner"
            className="mb-5 rounded-md border border-amber-300 bg-amber-50/60 px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 mb-1">You stepped back from your writing</p>
            <p className="text-[13.5px] text-stone-700 leading-snug">
              {entryConfirmation || returnContext.diagnosis || returnContext.reason
                || "Let's work this part out, then bring it back to your writing."}
            </p>
            {onResumeToComposition ? (
              <button onClick={onResumeToComposition} disabled={busy || completing}
                data-testid="fo-resume-composition"
                className={`mt-2.5 inline-flex items-center gap-1.5 rounded-sm px-4 py-2 text-[13px] font-medium transition-[background-color,transform] ${focalPartDone ? "bg-[#8C3A2A] text-white hover:bg-[#6B2C20] hover:-translate-y-px" : "border border-stone-300 text-stone-600 hover:border-[#8C3A2A] hover:text-[#8C3A2A]"}`}>
                Bring this back to my writing <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </motion.div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-6">
          {/* LEFT — the working structure (thinking -> writing) */}
          <div className="rounded-md border border-stone-200 bg-white/70 p-5" data-testid="fo-structure">
            <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-3">Your writing, taking shape</p>

            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">The idea organizing it all</p>
              <p data-testid="fo-thesis" className={`text-[14.5px] leading-snug border-l-2 pl-3 py-1 ${est.thesis ? "border-[#8C3A2A] text-stone-900 font-medium" : "border-stone-300 text-stone-600"}`}>
                {fo?.thesis || "Your thesis is still taking shape."}
                {fo?.thesis_changed ? <span className="ml-2 text-[10px] text-amber-700">(you reshaped this)</span> : null}
              </p>
              {est.thesis && !sufficient ? (
                <button onClick={() => send("", "reopen_thesis")} disabled={busy || completing}
                  data-testid="fo-reopen-thesis"
                  className="mt-1.5 text-[11px] text-stone-400 hover:text-[#8C3A2A] transition-colors">
                  Rethink my main idea
                </button>
              ) : null}
            </div>

            <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-2">
              Your major parts &mdash; what each does for your reader
            </p>
            <div className="space-y-2.5" data-testid="fo-units">
              {units.length === 0 ? (
                <p className="text-[13px] text-stone-500">Your parts will appear here as you work them out.</p>
              ) : units.map((u) => (
                <div key={u.key}
                  data-testid={`fo-unit-${u.key}`}
                  className={`rounded-sm border px-3 py-2.5 transition-colors ${u.focal ? "border-[#8C3A2A] bg-[#8C3A2A]/[0.04]" : u.worked_out ? "border-emerald-200 bg-emerald-50/40" : "border-stone-200 bg-white"}`}>
                  <div className="flex items-start gap-2">
                    <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${u.worked_out ? "bg-emerald-500 text-white" : u.focal ? "bg-[#8C3A2A] text-white" : "bg-stone-200 text-stone-500"}`}>
                      {u.worked_out ? <Check className="h-2.5 w-2.5" /> : "\u2022"}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13.5px] text-stone-900 leading-snug">{u.organizing_point || "(a part)"}</p>
                      <p className="text-[11.5px] text-stone-500 mt-0.5">
                        {u.worked_out || (u.relation_to_thesis && u.relation_to_thesis !== "probable")
                          ? <>Its job: <span className="text-stone-700">{u.relation_to_thesis && u.relation_to_thesis !== "probable" ? u.relation_to_thesis : "supports your main idea"}</span></>
                          : <span className="italic text-stone-400">{RELATION_HINT[u.relation_to_thesis] || "job not settled yet"}</span>}
                        {u.role && u.role !== "part" ? <span className="ml-1 text-stone-400">&middot; {u.role}</span> : null}
                      </p>
                      {u.reader_need ? <p className="text-[11px] text-stone-500 mt-0.5">Reader needs: {u.reader_need}</p> : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {fo?.development_plan ? (
              <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50/50 px-3 py-2.5" data-testid="fo-development-plan">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 mb-1">What you'll develop next</p>
                <p className="text-[13px] text-stone-700 leading-snug">{fo.development_plan}</p>
              </div>
            ) : null}

            {onStepBackToOT && !returnContext ? (
              <button onClick={() => onStepBackToOT({ stage: "OT", reason: "You want to rework the thinking behind this.", diagnosis: "" })}
                disabled={busy || completing}
                data-testid="fo-stepback-ot"
                className="mt-4 text-[11.5px] text-stone-400 hover:text-[#8C3A2A] transition-colors flex items-center gap-1">
                <CornerUpLeft className="h-3 w-3" /> This needs deeper rethinking &mdash; go back to my thinking map
              </button>
            ) : null}
          </div>

          {/* RIGHT — the FO coach dialogue */}
          <div className="rounded-md border border-stone-200 bg-white flex flex-col" data-testid="fo-coach">
            <div className="px-4 py-2.5 border-b border-stone-100">
              <p className="text-[11px] text-stone-500">
                <span className="font-mono-panel uppercase tracking-[0.15em] text-stone-400">Working on</span>{" "}
                <span className="text-stone-700">{coordLabel}</span>
              </p>
            </div>
            <div className="flex-1 min-h-[300px] max-h-[52vh] overflow-y-auto px-4 py-4 space-y-3" data-testid="fo-thread">
              {starting ? (
                <div className="flex items-center gap-2 text-stone-500 text-[14px]"><Loader2 className="h-4 w-4 animate-spin" /> Opening this step&hellip;</div>
              ) : null}
              {thread.map((m, i) => (
                <div key={i} className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={m.role === "tws" ? "fo-tws-msg" : "fo-student-msg"}
                    className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-[14px] leading-relaxed ${m.role === "student" ? "bg-[#8C3A2A] text-white" : "bg-stone-100 text-stone-800"}`}>
                    {m.role === "tws" ? renderBold(m.text) : m.text}
                  </div>
                </div>
              ))}
              {busy ? (
                <div className="flex justify-start">
                  <div className="rounded-lg bg-stone-100 px-3.5 py-2.5 text-stone-500 text-[14px] flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking&hellip;
                  </div>
                </div>
              ) : null}
              <div ref={endRef} />
            </div>

            {/* Contextual controls */}
            {!starting && !sufficient ? (
              <div className="px-4 pt-2 flex flex-wrap gap-2">
                {fo?.offer_advance && fo?.coordination === "part_function" ? (
                  <button onClick={() => send("", "advance_part")} disabled={busy || completing}
                    data-testid="fo-advance-part"
                    className="text-[12px] rounded-full border border-stone-300 px-3 py-1 text-stone-600 hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors">
                    Its job is clear &mdash; move on
                  </button>
                ) : null}
                {est.thesis && (est.parts_done >= 1 || est.parts_total === 0) ? (
                  <button onClick={() => send("", "ready_to_compose")} disabled={busy || completing}
                    data-testid="fo-ready-compose"
                    className="text-[12px] rounded-full border border-stone-300 px-3 py-1 text-stone-500 hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors">
                    I'm ready to start writing
                  </button>
                ) : null}
              </div>
            ) : null}

            {/* Input OR the continue-to-composition affordance */}
            {sufficient ? (
              <div className="p-4 border-t border-stone-100">
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-md bg-[#8C3A2A]/[0.05] border border-[#8C3A2A]/20 px-4 py-3">
                  <p className="text-[13.5px] text-stone-700 flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-[#8C3A2A] mt-0.5 shrink-0" />
                    You've worked out how your thinking becomes an essay for a reader. Now write it, part by part.
                  </p>
                  <button onClick={finish} disabled={completing}
                    data-testid="fo-continue-composition"
                    className="group mt-3 inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-6 py-3 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40">
                    {completing ? <><Loader2 className="h-4 w-4 animate-spin" /> Getting your writing ready&hellip;</> : <>Continue to Composition <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
                  </button>
                </motion.div>
              </div>
            ) : (
              <div className="p-4 border-t border-stone-100">
                <div className="flex items-end gap-2">
                  <textarea
                    data-testid="fo-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    disabled={busy || completing || starting}
                    rows={2}
                    placeholder="Say what you think in your own words&hellip;"
                    className="flex-1 resize-none rounded-md border border-stone-300 px-3 py-2 text-[14px] text-stone-900 focus:outline-none focus:border-[#8C3A2A] disabled:opacity-60"
                  />
                  <button onClick={() => send(input)} disabled={busy || completing || starting || !input.trim()}
                    data-testid="fo-send"
                    className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white px-4 py-2.5 rounded-md font-medium hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
